import type { Look } from '../types';
import type { Box, BuddhaMask } from '../buddha';

/** 主尊光明：每帧由 main 计算（坐标为 CSS px，亮度已乘开关、音频驱动） */
export interface BuddhaFrame {
  box: Box;
  head: { x: number; y: number; r: number; i: number; rings: number; color: string; rayN: number; rayI: number; rayW: number; rayLen: number; pulse: number };
  aura: { x: number; y: number; i: number; spread: number; flow: number; color: string };
  rim: { i: number; w: number; color: string };
  crown: { x: number; y: number; r: number; i: number; beam: number; color: string };
}

/** 每帧传入渲染器的动态量 */
export interface FrameDrive {
  time: number;
  /** 已乘以各自影响系数的驱动量 */
  glow: number;
  rays: number;
  rings: number;
  /** 起音脉冲 + 歌词换句脉冲 */
  pulse: number;
  /** 低频能量（用于云气流动） */
  low: number;
}

const VERT = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

/**
 * 佛光着色器：
 *   背景渐变 + 云气 → 头光（高斯光晕）+ 舟形背光 → 千道光束（双层反向旋转）
 *   → 莲瓣光环（随时间外扩，脉冲时加亮）→ 色调映射 + 暗角
 */
const FRAG = `
precision highp float;
uniform vec2 uRes;
uniform float uTime;
uniform vec3 uBgTop, uBgBottom, uPrimary, uSecondary, uAccent;
uniform float uNebula, uNebulaScale, uVignette;
uniform vec2 uCenter;
uniform float uHaloR, uHaloI, uBloom, uMandorla;
uniform float uRayCount, uRayI, uRaySharp, uRaySpeed, uRayLen, uShimmer;
uniform float uRingCount, uRingI, uRingSpacing, uRingWidth, uRingSpeed, uPetals, uPetalDepth, uRingPulse;
uniform float uExposure;
uniform float dGlow, dRays, dRings, dPulse, dLow;
// 主尊光明
uniform sampler2D uMask;
uniform float uHasMask;
uniform vec4 uBox;          // 佛像框 x, y, w, h（画布像素，y 向下）
uniform vec2 uMaskPad;
uniform vec2 uHeadC; uniform float uHeadR, uHeadI, uHeadRings, uHRayN, uHRayI, uHRayW, uHRayLen, uHeadPulse; uniform vec3 uHeadCol;
uniform vec2 uBodyC; uniform float uAuraI, uAuraSpread, uAuraFlow; uniform vec3 uAuraCol;
uniform float uRimI, uRimW; uniform vec3 uRimCol;
uniform vec2 uCrownC; uniform float uCrownR, uCrownI, uCrownBeam; uniform vec3 uCrownCol;

// pow(x, y) 在 x < 0 时 GLSL 未定义，真 GPU 上得 NaN，写入画布即为黑点（NaN × 0 仍是 NaN，强度为 0 也挡不住）。
// 平方一律用 sq；0.5 + 0.5 * cos 作底时 GPU 近似的 cos 可略小于 -1，用 c01 夹到 0。
float sq(float x) { return x * x; }
float c01(float x) { return max(0.0, 0.5 + 0.5 * x); }
float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float noise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0; float a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * noise(p); p = p * 2.03 + vec2(1.7, 9.2); a *= 0.5; }
  return v;
}

void main() {
  vec2 frag = gl_FragCoord.xy;
  vec2 uv = frag / uRes;
  float aspect = uRes.x / uRes.y;
  float minDim = min(uRes.x, uRes.y);
  // 以短边为单位的坐标，光心在 uCenter（屏幕坐标，y 向下）
  vec2 c = vec2(uCenter.x * uRes.x, (1.0 - uCenter.y) * uRes.y);
  vec2 p = (frag - c) / minDim;
  float r = length(p);
  float a = atan(p.y, p.x);

  // 背景
  vec3 col = mix(uBgBottom, uBgTop, smoothstep(0.0, 1.0, uv.y));
  float t = uTime;
  vec2 q = vec2(uv.x * aspect, uv.y) * uNebulaScale;
  float n1 = fbm(q + vec2(t * 0.012 + dLow * 0.05, -t * 0.008));
  float n2 = fbm(q * 1.7 - vec2(t * 0.01, t * 0.015) + n1 * 1.5);
  float neb = smoothstep(0.35, 0.95, n2) * uNebula;
  col += neb * mix(uSecondary, uAccent, n1) * 0.22 * (0.8 + 0.4 * dGlow);

  // 舟形背光：纵向拉伸的柔光
  vec2 pm = vec2(p.x * (1.0 + uMandorla * 1.2), p.y * (1.0 - uMandorla * 0.35));
  float rm = length(pm);

  // 头光
  float haloR = uHaloR * (1.0 + 0.18 * dGlow + 0.12 * dPulse);
  float core = exp(-pow(r / (haloR * 0.55), 2.0));
  float halo = exp(-pow(rm / haloR, 2.0));
  float bloom = exp(-rm / (haloR * 2.6)) * uBloom;
  float glowAmt = uHaloI * (0.7 + 0.45 * dGlow + 0.3 * dPulse);
  col += uPrimary * halo * 0.7 * glowAmt;
  col += mix(uPrimary, uSecondary, 0.55) * bloom * 0.5 * glowAmt;
  col += vec3(1.0, 0.96, 0.88) * core * 0.6 * glowAmt;

  // 光束：两层反向旋转，角度噪声让每道光长短不一
  float rays = 0.0;
  if (uRayCount > 0.5) {
    float a1 = a + t * uRaySpeed;
    float a2 = a - t * uRaySpeed * 0.7 + 0.37;
    float b1 = pow(c01(cos(a1 * uRayCount)), uRaySharp);
    float b2 = pow(c01(cos(a2 * (uRayCount * 0.5 + 3.0))), uRaySharp * 0.6);
    float var1 = 0.55 + 0.45 * noise(vec2(a1 * uRayCount / 6.2832 * 0.9, t * 0.15 * uShimmer));
    float lenFall = exp(-r / max(0.05, uRayLen * (0.55 + 0.35 * dRays)));
    float inner = smoothstep(haloR * 0.25, haloR * 0.9, r);
    rays = (b1 * var1 + b2 * 0.45) * lenFall * inner;
    float rayAmt = uRayI * (0.6 + 0.9 * dRays + 0.4 * dPulse);
    col += mix(uPrimary, uSecondary, smoothstep(0.1, 0.8, r)) * rays * rayAmt;
  }

  // 莲瓣光环：持续外扩，脉冲时更亮、更宽
  if (uRingCount > 0.5 && uRingI > 0.0) {
    float ringsAcc = 0.0;
    for (int i = 0; i < 12; i++) {
      float fi = float(i);
      if (fi >= uRingCount) break;
      float phase = fract(fi / uRingCount + t * uRingSpeed);
      float rr = haloR * 1.05 + phase * uRingSpacing * uRingCount;
      float w = uRingWidth * (1.0 + 1.5 * dRings + uRingPulse * dPulse * 2.0) * (0.6 + phase);
      // 莲瓣轮廓：半径按 |cos| 起伏，形成一圈圈花瓣；相邻光环错开半瓣、缓慢反向转动
      float rot = (mod(fi, 2.0) < 0.5 ? 1.0 : -1.0) * t * 0.03 + fi * 3.14159 / max(uPetals, 1.0);
      float scallop = uPetals > 0.5 ? pow(abs(cos((a + rot) * uPetals * 0.5)), 0.7) : 1.0;
      float rShape = rr * (1.0 - uPetalDepth + uPetalDepth * scallop);
      float band = exp(-sq((r - rShape) / w));
      float fade = smoothstep(0.0, 0.12, phase) * (1.0 - smoothstep(0.65, 1.0, phase));
      ringsAcc += band * fade * (0.65 + 0.35 * scallop);
    }
    float ringAmt = uRingI * (0.55 + 1.0 * dRings + uRingPulse * dPulse);
    col += mix(uPrimary, uAccent, 0.18) * ringsAcc * ringAmt;
  }

  // ---------------- 主尊光明（均在佛像图层之后，佛身本体由 DOM 图像清晰呈现） ----------------
  vec2 P = vec2(frag.x, uRes.y - frag.y);
  vec3 white = vec3(1.0, 0.975, 0.92);
  vec4 m = vec4(0.0);
  if (uHasMask > 0.5) {
    vec2 iuv = (P - uBox.xy) / uBox.zw;
    vec2 muv = (iuv + uMaskPad) / (1.0 + 2.0 * uMaskPad);
    if (muv.x > 0.0 && muv.x < 1.0 && muv.y > 0.0 && muv.y < 1.0) m = texture2D(uMask, muv);
  }
  float S = uBox.w;

  // 周身金白光：贴身形的层次光 + 纵向舟形柔光 + 宽柔流动光带
  if (uAuraI > 0.001) {
    vec2 bd = (P - uBodyC) / S;
    float ell = exp(-(sq(bd.x / (0.3 * uAuraSpread)) + sq(bd.y / (0.6 * uAuraSpread))));
    float ell2 = exp(-(sq(bd.x / (0.55 * uAuraSpread)) + sq(bd.y / (0.85 * uAuraSpread))));
    // 莲座处减弱，避免底部过曝；光明主要自上身舒展
    float vfade = 1.0 - 0.5 * smoothstep(0.62, 1.0, (P.y - uBox.y) / uBox.w);
    float shape = (m.b * 0.35 + m.a * 0.75) * vfade;
    float ang = atan(bd.y, bd.x);
    float flowR = length(bd) * 2.4 - t * 0.05;
    float flowN = fbm(vec2(ang * 1.6 + 3.0, flowR));
    // atan 在 ±π 处跳变 2π，而 fbm 非周期；在 +π 侧渐变到 -π 分支采样，消除周身光带接缝。
    float seamW = smoothstep(3.14159265 - 0.6, 3.14159265, ang);
    if (seamW > 0.0) {
      float flowB = fbm(vec2((ang - 6.2831853) * 1.6 + 3.0, flowR));
      flowN = 0.484 + ((flowN - 0.484) * (1.0 - seamW) + (flowB - 0.484) * seamW) / sqrt(sq(1.0 - seamW) + sq(seamW));
    }
    float lobes = pow(c01(cos(ang * 9.0 + 0.35 * sin(t * 0.07))), 3.0);
    float streams = mix(1.0, 0.45 + 0.55 * lobes + 0.6 * flowN, uAuraFlow);
    float aura = (shape * 0.75 + ell * 0.45 + ell2 * 0.18) * streams;
    vec3 ac = mix(uAuraCol, white, clamp(m.b * 0.9, 0.0, 1.0));
    col += ac * aura * uAuraI;
  }

  // 佛身轮廓光：剪影外缘一道明显而柔和的光边（透过图像半透明边缘也会显出）
  if (uRimI > 0.001) {
    float e = mix(m.g, m.b, uRimW);
    float rim = pow(clamp(e * 2.0, 0.0, 1.0), 1.7);
    col += mix(uRimCol, white, 0.35) * (rim * 0.9 + m.b * 0.18) * uRimI;
  }

  // 佛顶光：肉髻上方的光晕；佛顶放光为向上舒展的柔和光束
  if (uCrownI > 0.001) {
    vec2 cd = (P - uCrownC) / uCrownR;
    float cr = length(cd);
    float cg = exp(-cr * cr * 1.2) * 1.3 + exp(-cr * 0.75) * 0.3;
    float beam = 0.0;
    if (uCrownBeam > 0.001) {
      float ang = atan(cd.x, -cd.y);
      float fan = exp(-sq(ang / 0.16)) + 0.5 * exp(-sq((abs(ang) - 0.34) / 0.1)) + 0.25 * exp(-sq((abs(ang) - 0.68) / 0.1));
      beam = fan * smoothstep(0.4, 1.4, cr) * exp(-cr / 9.0) * (0.85 + 0.15 * sin(t * 0.6 - cr * 0.3));
    }
    col += mix(uCrownCol, white, 0.4) * (cg + beam * uCrownBeam * 1.4) * uCrownI;
  }

  // 头部圆光：以额头为圆心，头后清净圆盘 + 多层同心圆环 + 少量宽柔远伸光线
  if (uHeadI > 0.001) {
    vec2 hd = P - uHeadC;
    float hr = length(hd) / uHeadR;
    float onePx = 1.0 / uHeadR;
    float wr = max(0.016, 1.4 * onePx);
    float disc = (1.0 - smoothstep(0.93, 1.0, hr)) * (0.5 + 0.75 * exp(-hr * hr * 2.0));
    float ringOuter = exp(-sq((hr - 1.0) / (wr * 1.8)));
    float ring2 = exp(-sq((hr - 0.87) / wr));
    float ring3 = exp(-sq((hr - 0.74) / (wr * 0.9)));
    float ring4 = exp(-sq((hr - 0.62) / (wr * 0.8)));
    float gapDark = exp(-sq((hr - 0.935) / (wr * 1.6))) + 0.6 * exp(-sq((hr - 0.805) / (wr * 1.2)));
    disc *= 1.0 - 0.55 * uHeadRings * clamp(gapDark, 0.0, 1.0);
    float rings = (ring2 * 0.9 + ring3 * 0.65 + ring4 * 0.45) * uHeadRings;
    float rimR = ringOuter * (0.9 + 0.9 * uHeadRings);
    float outer = exp(-max(0.0, hr - 1.0) / 0.22) * smoothstep(0.92, 1.02, hr) * 0.65;
    float rip = uHeadPulse * exp(-sq((hr - (1.04 + 0.5 * (1.0 - uHeadPulse))) / 0.045)) * 0.55;
    vec3 hc = uHeadCol;
    vec3 deepGold = hc * vec3(1.0, 0.82, 0.55);
    vec3 hl = mix(hc, white, clamp(0.75 - 0.45 * hr, 0.0, 1.0)) * disc + mix(hc, white, 0.2) * rings + mix(deepGold, hc, 0.4) * rimR * 1.3 + hc * outer + mix(hc, white, 0.5) * rip;
    if (uHRayN > 0.5 && uHRayI > 0.001) {
      float seg = 6.2831853 / uHRayN;
      float aa = atan(hd.y, hd.x) + 1.5707963 + 0.02 * sin(t * 0.08);
      float k = floor(aa / seg + 0.5);
      float da = aa - k * seg;
      float dist = abs(sin(da)) * length(hd);
      float w = uHeadR * (0.03 + 0.035 * hr) * uHRayW;
      float beamR = exp(-sq(dist / w));
      float var = 0.6 + 0.4 * hash(vec2(k, 7.0));
      float radial = smoothstep(0.97, 1.15, hr) * exp(-(hr - 1.0) / (max(0.05, uHRayLen) * 3.2 * (0.75 + 0.5 * var)));
      float breath = 0.85 + 0.15 * sin(t * 0.35 + k * 1.7);
      hl += mix(hc, white, 0.45) * beamR * radial * var * breath * uHRayI;
    }
    col += hl * uHeadI;
  }

  // 细微闪点
  float spark = pow(noise(frag * 0.16 + vec2(t * 0.4, -t * 0.9)), 36.0) * (halo * 0.6 + rays) * 1.6;
  col += uPrimary * spark;

  // 色调映射 + 暗角
  col *= uExposure;
  col = vec3(1.0) - exp(-col * 1.25);
  col = pow(col, vec3(1.0 / 2.2));
  vec2 vv = uv - 0.5;
  float vig = 1.0 - uVignette * smoothstep(0.25, 0.95, length(vv * vec2(aspect > 1.0 ? 1.0 : 1.3, aspect > 1.0 ? 1.25 : 1.0)) * 1.25);
  col *= vig;
  // 抖动，避免渐变色带
  col += (hash(frag + fract(t)) - 0.5) / 255.0;
  gl_FragColor = vec4(col, 1.0);
}
`;

type U = Record<string, WebGLUniformLocation | null>;

export class GLRenderer {
  readonly ok: boolean;
  private gl: WebGLRenderingContext | null;
  private prog: WebGLProgram | null = null;
  private u: U = {};
  private maskTex: WebGLTexture | null = null;
  private mask: BuddhaMask | null = null;
  private kx = 1;
  private ky = 1;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.gl = canvas.getContext('webgl', { antialias: false, premultipliedAlpha: false, alpha: false, powerPreference: 'high-performance' });
    this.ok = !!this.gl && this.init();
  }

  private init(): boolean {
    const gl = this.gl!;
    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(s));
        return null;
      }
      return s;
    };
    const vs = sh(gl.VERTEX_SHADER, VERT);
    const fs = sh(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return false;
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(prog));
      return false;
    }
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const n = gl.getProgramParameter(prog, gl.ACTIVE_UNIFORMS) as number;
    for (let i = 0; i < n; i++) {
      const info = gl.getActiveUniform(prog, i)!;
      this.u[info.name] = gl.getUniformLocation(prog, info.name);
    }
    this.prog = prog;
    return true;
  }

  /** 上传佛像剪影遮罩（R 清晰 / G 小模糊 / B 中模糊 / A 大模糊） */
  setMask(mask: BuddhaMask): void {
    const gl = this.gl;
    if (!gl || !this.prog) return;
    const tex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, mask.width, mask.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, mask.data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(this.u.uMask, 0);
    this.maskTex = tex;
    this.mask = mask;
  }

  resize(cssW: number, cssH: number, scale: number): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(2, Math.round(cssW * dpr * scale));
    const h = Math.max(2, Math.round(cssH * dpr * scale));
    this.kx = w / Math.max(1, cssW);
    this.ky = h / Math.max(1, cssH);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  render(look: Look, d: FrameDrive, b?: BuddhaFrame): void {
    const gl = this.gl;
    if (!gl || !this.prog) return;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    const u = this.u;
    const f1 = (k: string, v: number) => gl.uniform1f(u[k], v);
    const c3 = (k: string, hex: string) => {
      const [r, g, b] = hexToRgb(hex);
      gl.uniform3f(u[k], r, g, b);
    };
    gl.uniform2f(u.uRes, this.canvas.width, this.canvas.height);
    f1('uTime', d.time);
    c3('uBgTop', look.background.top);
    c3('uBgBottom', look.background.bottom);
    c3('uPrimary', look.colors.primary);
    c3('uSecondary', look.colors.secondary);
    c3('uAccent', look.colors.accent);
    f1('uNebula', look.background.nebula);
    f1('uNebulaScale', look.background.nebulaScale);
    f1('uVignette', look.background.vignette);
    gl.uniform2f(u.uCenter, look.halo.x, look.halo.y);
    f1('uHaloR', look.halo.radius);
    f1('uHaloI', look.halo.intensity);
    f1('uBloom', look.halo.bloom);
    f1('uMandorla', look.halo.mandorla);
    f1('uRayCount', Math.round(look.rays.count));
    f1('uRayI', look.rays.intensity);
    f1('uRaySharp', look.rays.sharpness);
    f1('uRaySpeed', look.rays.speed);
    f1('uRayLen', look.rays.length);
    f1('uShimmer', look.rays.shimmer);
    f1('uRingCount', Math.round(look.rings.count));
    f1('uRingI', look.rings.intensity);
    f1('uRingSpacing', look.rings.spacing);
    f1('uRingWidth', look.rings.width);
    f1('uRingSpeed', look.rings.speed);
    f1('uPetals', Math.round(look.rings.petals));
    f1('uPetalDepth', look.rings.petalDepth);
    f1('uRingPulse', look.rings.pulse);
    f1('uExposure', look.render.exposure);
    f1('dGlow', d.glow);
    f1('dRays', d.rays);
    f1('dRings', d.rings);
    f1('dPulse', d.pulse);
    f1('dLow', d.low);
    this.setBuddha(b);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  private setBuddha(b: BuddhaFrame | undefined): void {
    const gl = this.gl!;
    const u = this.u;
    const kx = this.kx;
    const ky = this.ky;
    const f1 = (k: string, v: number) => gl.uniform1f(u[k], v);
    const c3 = (k: string, hex: string) => {
      const [r, g, bb] = hexToRgb(hex);
      gl.uniform3f(u[k], r, g, bb);
    };
    if (!b) {
      for (const k of ['uHeadI', 'uAuraI', 'uRimI', 'uCrownI', 'uHasMask']) f1(k, 0);
      return;
    }
    const hasMask = !!(this.mask && this.maskTex);
    if (hasMask) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.maskTex);
      gl.uniform2f(u.uMaskPad, this.mask!.padU, this.mask!.padV);
    }
    f1('uHasMask', hasMask ? 1 : 0);
    gl.uniform4f(u.uBox, b.box.x * kx, b.box.y * ky, b.box.w * kx, b.box.h * ky);
    const H = b.head;
    gl.uniform2f(u.uHeadC, H.x * kx, H.y * ky);
    f1('uHeadR', Math.max(1, H.r * kx));
    f1('uHeadI', H.i);
    f1('uHeadRings', H.rings);
    c3('uHeadCol', H.color);
    f1('uHRayN', Math.round(H.rayN));
    f1('uHRayI', H.rayI);
    f1('uHRayW', H.rayW);
    f1('uHRayLen', H.rayLen);
    f1('uHeadPulse', H.pulse);
    const A = b.aura;
    gl.uniform2f(u.uBodyC, A.x * kx, A.y * ky);
    f1('uAuraI', A.i);
    f1('uAuraSpread', A.spread);
    f1('uAuraFlow', A.flow);
    c3('uAuraCol', A.color);
    f1('uRimI', hasMask ? b.rim.i : 0);
    f1('uRimW', b.rim.w);
    c3('uRimCol', b.rim.color);
    const C = b.crown;
    gl.uniform2f(u.uCrownC, C.x * kx, C.y * ky);
    f1('uCrownR', Math.max(1, C.r * kx));
    f1('uCrownI', C.i);
    f1('uCrownBeam', C.beam);
    c3('uCrownCol', C.color);
  }
}

const rgbCache = new Map<string, [number, number, number]>();
export function hexToRgb(hex: string): [number, number, number] {
  const hit = rgbCache.get(hex);
  if (hit) return hit;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h.slice(0, 6), 16) || 0;
  // sRGB → 线性，便于在着色器里做加色
  const lin = (v: number) => Math.pow(v / 255, 2.2);
  const out: [number, number, number] = [lin((n >> 16) & 255), lin((n >> 8) & 255), lin(n & 255)];
  rgbCache.set(hex, out);
  return out;
}
